import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EventType } from "./SchedulingTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

const EventTooltip: React.FC<{ event: EventType, x: number, y: number }> = ({ event, x, y }) => {
    const { t } = useTranslation();
    const [pos, setPosition] = useState({ top: 0, left: 0 });
    const [visible, setVisible] = useState(false);
    const [ready, setReady] = useState(false);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Show the tooltip after a slight delay
    useEffect(() => {
        setVisible(false);
        setReady(false);

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        if (event) {
            const id = setTimeout(() => {
                setVisible(true);
            }, 125);

            timerRef.current = id;
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [event]);

    // Calculate the last given position and set ready to prevent flickering
    useEffect(() => {
        if (!event || !tooltipRef.current) return;

        // Get the tooltip rect
        const rect = tooltipRef.current.getBoundingClientRect();

        // Get the view width and height
        const viewWidth = window.innerWidth;
        const viewHeight = window.innerHeight;

        // Set the offset from the side to 12 pixels
        const offset = 12;

        // Get the left and top position with the offset
        let left = x + offset;
        let top = y + offset;

        // Prevent right overflow
        if (left + rect.width > viewWidth) {
            left = x - rect.width - offset;
        }

        // Prevent bottom overflow
        if (top + rect.height > viewHeight) {
            top = y - rect.height - offset;
        }

        // Clamp
        if (left < offset) left = offset;
        if (top < offset) top = offset;

        setPosition({ top, left });
        setReady(true);
    }, [event, x, y]);

    if (!event) return null;

    // Determine is visible state
    const isVisible = visible && ready;

    return (
        <div ref={tooltipRef}
             className="event-tooltip"
             style={{
                position: "fixed",
                top: ready ? pos.top : -9999,
                left: ready ? pos.left : -9999,
                visibility: isVisible ? "visible" : "hidden",
                opacity: isVisible ? 1 : 0,
            }}>
            {(event.type === "appointment" || event.type === "timerow") && (
                <div>
                    <div className="tooltip-header">
                        <h6>
                            <span>
                                {event.job.type_name}
                                {event.job.number && <span> {event.job.number}</span>}
                            </span>
                            {event.type === "appointment" && event.locked && (
                                <span className="tooltip-lock-icon">
                                    <FontAwesomeIcon icon={faLock} />
                                </span>
                            )}
                        </h6>
                        {event.job.internal_reference && (
                            <div className="tooltip-reference">
                                {event.job.internal_reference}
                            </div>
                        )}
                        {(event.startTime || event.endTime) && (
                            <div className="tooltip-time">
                                {event.startTime} – {event.endTime}
                            </div>
                        )}

                        <div className="label-badge-container">
                            {event.job.labels?.map((label, index) => (
                                <div key={index} className={`label-badge ${label.color}`}>
                                    {label.name}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="tooltip-content">
                        <div className="client-name">{event.job.client_name}</div>

                        <div>
                            {event.job.work_location_street}
                            {event.job.work_location_house_number && (
                                <span> {event.job.work_location_house_number}</span>
                            )}
                        </div>

                        <div>
                            {event.job.work_location_postal_code && (
                                <span>{event.job.work_location_postal_code}</span>
                            )}
                            {event.job.work_location_city && (
                                <span> {event.job.work_location_city}</span>
                            )}
                        </div>

                        {event.job.main_description && (
                            <div className="main-description">{event.job.main_description}</div>
                        )}
                    </div>

                    <div className="status-badge-container">
                        <div className={`status-badge ${event.job.status_condition ?? event.job.status_name}`}>
                            {t(`workflow.default_status_names.${event.job.status_name}`, {
                                defaultValue: event.job.status_name
                            })}
                        </div>
                    </div>
                </div>
            )}

            {event.type === "activity" && (
                <div className="activity-event">
                    <div className="tooltip-header">
                        <h6>{event.time_type_name}</h6>
                        <div className="tooltip-time">
                            {event.startTime} – {event.endTime}
                        </div>
                    </div>

                    {event.note && <div className="tooltip-content">{event.note}</div>}
                </div>
            )}
        </div>
    );
};

export default EventTooltip;